import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {OmitStrict, PartiallyOptional} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    asArray,
    hideableValueCreateHidden,
    hideableValueGetVisibleValue,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce,
    iterableMapToArray
} from '@wix/devzai-utils-common';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    BymoPagePropsSpecialContentSettingMetadata,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeActionLayout,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    doppeDtoProductGetMediaItems,
    DoppeDtoStoreProduct,
    DoppeExternalProductProvider,
    doppeExternalProductProviderResolveBusinessEntityReference,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemSettingsCreateDefault,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
    DoppeProductsExternalDataSource,
    doppeProductsExternalDataSourceResolveBusinessEntityReferences
} from '../../client-server-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    DoppeActionCTAType,
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeLinkSpecEntityType,
    DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {
    DoppeActionTypeStoreProduct
} from '../doppe-action-type-hopp-store-product/doppe-action-type-store-product';
import {
    convertDoppeWixProductToStoreProductSettings
} from '../doppe-action-type-hopp-store-product/doppe-action-type-store-product';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';

export namespace DoppeActionTypeStoreProducts {

    export interface ProductSettings extends
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        OmitStrict<DoppeActionTypeStoreProduct.ActionSettings, 'ctaButtons' | 'sections'> {
    }

    export interface ActionSettings extends
        DoppeListActionSettings<ProductSettings>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeProductsExternalDataSource> {
        itemShowPrice: boolean;
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
        previewShowCoupon: boolean;
    }
}

export const doppeActionTypeStoreProducts = doppeActionTypeDefineMetadata<DoppeActionTypeStoreProducts.ActionSettings>({
    id: 'io.bymo.action/store-products',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            layout = DoppeActionLayout.Column,
            itemShowPrice= true,
            previewShowCoupon= true,
            previewShowPrice= true,
            previewShowDescription= true,
            previewShowTitle= true,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            itemShowPrice: itemShowPrice,
            previewShowCoupon: previewShowCoupon,
            previewShowPrice: previewShowPrice,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(action.settings),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({}, action.settings),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings({
                ...action.settings,
                layout: layout,
                listDataItems: listDataItems.map(product => {

                    const {
                        ctaButtonsOverridesMap,
                        customCtaButtons,
                        mainMediaViewSettings,
                        mainMediaViewLayout,
                        id,
                        deleted,
                        displayInSearch,
                        enabled,
                        ...restProduct
                    } = product;

                    return {
                        ...doppeActionGetMediaViewDefaultSettingsMigration(product),
                        ...doppeListActionListItemWithCtaButtonsMigration(product, false),
                        ...doppeListActionListItemWithSectionsMigration(product),
                        ...doppeListActionListItemSettingsMigrateBaseSettings(product),
                        ...restProduct as PartiallyOptional<DoppeActionTypeStoreProducts.ProductSettings, 'mainMediaViewSettings' | 'mainMediaViewLayout'  | 'ctaButtonsOverridesMap' |'customCtaButtons' | 'sectionsOverridesMap' | 'customSections'>
                    }
                }),
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeStoreProducts.ActionSettings
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            externalDataSourceItemsCount: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.BuyNow]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            productTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            productImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.BuyNow]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            couponCode: true,
        }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(iterableMapToArray(actionSettings.listDataItems, (product, skip) => {
            const mediaItems = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(product.productImage, []) ?? []));
            return mediaItems.length > 0 ? mediaItems : skip;
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        const product = actionSettings.listDataItems[0];
        const mediaItems = product ? arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(product.productImage, []) ?? [])) : [];
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, mediaItems[0]);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [
                doppeProductsExternalDataSourceResolveBusinessEntityReferences(externalDataSource)
            ];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (product, skip) => {
                return product.productProvider !== null && product.productProviderId !== null ?
                    doppeExternalProductProviderResolveBusinessEntityReference(
                        product.productProvider,
                        product.productProviderId
                    ) : skip
            });
        }
    },
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveCouponDataFromListItem: listItem => {
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(listItem.couponCode, ''),
            couponCode: hideableValueGetVisibleValue(listItem.couponCode, ''),
            couponDescription: '',
            couponTitle: hideableValueGetVisibleValue(listItem.title, ''),
        }
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            // Try to resolve the product entity from business entities data
            const productEntity = item.productProviderId ? bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                {businessEntity: DoppeBusinessEntity.HoppProduct, id: item.productProviderId}
            ) : undefined;

            // Check if we have valid entity data
            const hasValidEntity = productEntity && !doppeBusinessEntityDtoIsNotFound(productEntity);

            // Resolve values from entity or fall back to stored settings
            const title = hasValidEntity
                ? (productEntity.name ?? hideableValueGetVisibleValue(item.productTitle, ''))
                : hideableValueGetVisibleValue(item.productTitle, '');

            const description = hasValidEntity
                ? (productEntity.descriptionPlainText ?? hideableValueGetVisibleValue(item.description, ''))
                : hideableValueGetVisibleValue(item.description, '');

            const ribbon = hasValidEntity
                ? (productEntity.ribbon ?? hideableValueGetVisibleValue(item.ribbon, ''))
                : hideableValueGetVisibleValue(item.ribbon, '');

            // Resolve image from entity or fall back to stored settings
            const entityImages = hasValidEntity ? doppeDtoProductGetMediaItems(productEntity) : null;
            const storedImages = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(item.productImage, []) ?? []));
            const image = (entityImages && entityImages.length > 0 ? entityImages[0] : storedImages[0]) ?? null;

            // Calculate URL from link spec
            const url = doppeActionCtaButtonsResolveMainURL(
                doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item),
                {
                    sourceLinkSpec: item.productProviderId ? {
                        type: DoppeLinkSpecType.Entity,
                        entityType: DoppeLinkSpecEntityType.Product,
                        entityId: item.productProviderId
                    } : null,
                    viewerLang: viewerLang,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    bymoPageContextProps: bymoPageContextProps
                }
            ) ?? null;

            return {
                title: title,
                action: action,
                couponCode: item.couponCode ?? hideableValueCreateHidden(''),
                description: hideableValueCreateHidden(description),
                image: image,
                itemId: item.id,
                ribbon: hideableValueCreateHidden(ribbon),
                url: url,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.productImage) {
                    const images = item.productImage?? [];
                    item.productImage = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    })
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});


export function convertDoppeWixProductToStoreProductsSettings(
    product: DoppeDtoStoreProduct,
    productProvider?: DoppeExternalProductProvider,
): DoppeActionTypeStoreProducts.ProductSettings {
    const productSettings = convertDoppeWixProductToStoreProductSettings({
        storeProduct: product,
        productProvider: productProvider,
    });

    const {
        ctaButtons,
        ...restProductSettings
    } = productSettings;

    return {
        ...restProductSettings,
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionListItemSettingsCreateDefault(product.id),
    }
}